import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = [];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    onDocumentLoaded(this.#preloadImages);

    // Mobile menu toggle functionality
    this.#initMobileMenu();
  }

  /**
   * Initialize mobile menu toggle functionality
   */
  #initMobileMenu() {
    const navbar = this.querySelector('.navbar, .nb');
    const toggleButtons = this.querySelectorAll('[data-mobile-menu-toggle]');

    if (!navbar || !toggleButtons.length) return;

    toggleButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.#toggleMobileMenu(navbar, toggleButtons);
      }, { signal: this.#abortController.signal });
    });

    // Close menu when clicking on a mobile menu link (only links without submenus)
    const mobileLinks = this.querySelectorAll('.navbar__mobile-menu-link, .nb__mobile-menu-link');
    mobileLinks.forEach((link) => {
      // Skip links inside a link-row (they have a separate submenu toggle)
      if (link.closest('.navbar__mobile-menu-link-row, .nb__mobile-menu-link-row')) return;
      link.addEventListener('click', () => {
        this.#closeMobileMenu(navbar, toggleButtons);
      }, { signal: this.#abortController.signal });
    });

    // Mobile submenu accordion toggles
    this.#initMobileSubmenus();

    // Close menu when clicking on the backdrop overlay
    const backdrop = this.querySelector('[data-mobile-menu-backdrop]');
    if (backdrop) {
      backdrop.addEventListener('click', () => {
        this.#closeMobileMenu(navbar, toggleButtons);
      }, { signal: this.#abortController.signal });
    }
  }

  /**
   * Initialize mobile submenu accordion toggles
   */
  #initMobileSubmenus() {
    const submenuToggles = this.querySelectorAll('[data-mobile-submenu-toggle]');

    submenuToggles.forEach((toggle) => {
      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        const parentItem = toggle.closest('.navbar__mobile-menu-item, .nb__mobile-menu-item');
        if (!parentItem) return;

        const submenu = parentItem.querySelector('.navbar__mobile-submenu, .nb__mobile-submenu');
        if (!submenu) return;

        const isOpen = submenu.classList.contains('navbar__mobile-submenu--open');

        if (isOpen) {
          submenu.classList.remove('navbar__mobile-submenu--open', 'nb__mobile-submenu--open');
          toggle.setAttribute('aria-expanded', 'false');
        } else {
          submenu.classList.add('navbar__mobile-submenu--open', 'nb__mobile-submenu--open');
          toggle.setAttribute('aria-expanded', 'true');
        }
      }, { signal: this.#abortController.signal });
    });
  }

  /**
   * Toggle mobile menu open/closed
   * @param {Element} navbar
   * @param {NodeListOf<Element>} toggleButtons
   */
  #toggleMobileMenu(navbar, toggleButtons) {
    const isOpen = navbar.classList.contains('navbar--open');

    if (isOpen) {
      this.#closeMobileMenu(navbar, toggleButtons);
    } else {
      this.#openMobileMenu(navbar, toggleButtons);
    }
  }

  /**
   * Open mobile menu
   * @param {Element} navbar
   * @param {NodeListOf<Element>} toggleButtons
   */
  #openMobileMenu(navbar, toggleButtons) {
    navbar.classList.add('navbar--open', 'nb--open');
    toggleButtons.forEach((btn) => {
      btn.setAttribute('aria-expanded', 'true');
      btn.setAttribute('aria-label', 'Close menu');
    });
    document.body.style.overflow = 'hidden';

    // Show backdrop overlay
    const backdrop = this.querySelector('[data-mobile-menu-backdrop]');
    if (backdrop) {
      backdrop.classList.add('navbar__backdrop--active', 'nb__backdrop--active');
    }
  }

  /**
   * Close mobile menu
   * @param {Element} navbar
   * @param {NodeListOf<Element>} toggleButtons
   */
  #closeMobileMenu(navbar, toggleButtons) {
    navbar.classList.remove('navbar--open', 'nb--open');
    toggleButtons.forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
      btn.setAttribute('aria-label', 'Open menu');
    });
    document.body.style.overflow = '';

    // Hide backdrop overlay
    const backdrop = this.querySelector('[data-mobile-menu-backdrop]');
    if (backdrop) {
      backdrop.classList.remove('navbar__backdrop--active', 'nb__backdrop--active');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    // Mark submenu as active for content-visibility optimization
    if (submenu) {
      submenu.dataset.active = '';
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
      // Remove active state from submenu after animation completes
      if (submenu) {
        delete submenu.dataset.active;
      }
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
